import * as React from 'react';
import { connect } from 'react-redux';
import { withRouter } from '@module/shared/routerCompat';

import { Header, LeftNavigation, MUIBreadcrumb } from '@abb/arcadia-ui-react';
import { getApiVersionInfo, getUiVersion } from '@service/versionService';

/* tslint:disable:jsx-no-lambda */
export class LayoutComponent extends React.Component<any, any> {
  state = {
    isSidebarOpen: false,
    isAvatarMenuOpen: false,
    uiVersion: getUiVersion(),
    apiVersion: 'unknown'
  }

  avatarMenuRef: React.RefObject<HTMLDivElement | null> = React.createRef();

  componentDidMount() {
    document.addEventListener('click', this.handleClickOutside);
    this.loadVersions();
  }

  componentWillUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
  }

  loadVersions = async () => {
    const { apiVersion } = await getApiVersionInfo();
    this.setState({ apiVersion });
  }

  handleClickOutside = (event: MouseEvent) => {
    if (this.avatarMenuRef.current && !this.avatarMenuRef.current.contains(event.target as Node)) {
      this.setState({ isAvatarMenuOpen: false });
    }
  }

  toggleAvatarMenu = () => {
    this.setState((prevState: any) => ({
      isAvatarMenuOpen: !prevState.isAvatarMenuOpen
    }));
  }

  handleLogout = () => {
    this.setState({ isAvatarMenuOpen: false });
    this.props.history.push('/logout');
  }

  goHome = () => {
    this.props.history.push('/');
  }

  onClickLeftNavigationItem = (item: any) => {
    // Handle string path passed directly
    if (typeof item === 'string' && item.startsWith('/')) {
      this.props.history.push(item);
    }
    // Handle object with path property
    else if (item?.path) {
      this.props.history.push(item.path);
    }
    // Handle object with onClick callback
    else if (item?.onClick) {
      item.onClick();
    }
    // Default to home
    else {
      this.props.history.push('/');
    }
  }

  onHamburgerMenuClick = () => {
    this.setState((prevState: any) => ({
      isSidebarOpen: !prevState.isSidebarOpen
    }));
  }

  onClickBreadcrumb = (item: any) => {
    if (item && typeof item === 'object' && item.url) {
      this.props.history.push(item.url);
      return;
    }

    if (typeof item === 'string' && item.startsWith('/')) {
      this.props.history.push(item);
    }
  }

  getBreadcrumbConfig = (pathname: string, siteNameFromRoute?: string, siteNameFromStore?: string) => {
    const homeItem = {
      icon: 'icon_abb_dias icon-abb_home',
      label: 'Home',
      url: '/'
    };

    if (pathname === '/') {
      return {
        previousPages: [],
        currentPage: {
          label: 'Home'
        }
      };
    }

    const siteDrilldownMatch = pathname.match(/^\/Licenses\/([^/]+)$/i);
    if (siteDrilldownMatch) {
      const siteName = siteNameFromRoute || siteNameFromStore || 'Site Drilldown';
      return {
        previousPages: [homeItem],
        currentPage: {
          label: siteName
        }
      };
    }

    if (pathname === '/register') {
      return {
        previousPages: [homeItem],
        currentPage: {
          label: 'Search ServIS'
        }
      };
    }

    if (pathname === '/UserManagement') {
      return {
        previousPages: [homeItem],
        currentPage: {
          label: 'User Management'
        }
      };
    }

    const fallbackLabel = pathname
      .split('/')
      .filter(Boolean)
      .map((part: string) => decodeURIComponent(part))
      .join(' / ');

    return {
      previousPages: [homeItem],
      currentPage: {
        label: fallbackLabel || 'Home'
      }
    };
  }

  render() {
    const { location } = this.props;
    const isLoginPage = this.props.location?.pathname === '/login';
    const isLoggedIn = Boolean(this.props.loggedInUser);
    const showSidebar = isLoggedIn && !isLoginPage;
    const userProfile = this.props.loggedInUser?.profile;
    const siteNameFromRoute = location?.state?.siteName;
    const breadcrumbConfig = this.getBreadcrumbConfig(location?.pathname || '/', siteNameFromRoute, this.props.currentSiteName);
    const siteDashboardNavItem = {
      id: 'site-dashboard',
      navText: 'Site Dashboard',
      icon: 'icon_abb_dias icon-abb_Company',
      path: '/'
    };

    const sidebarItems: any[] = [siteDashboardNavItem];
    if (this.props.userPermissions?.isAdmin) {
      sidebarItems.push({
        id: 'user-management',
        navText: 'User Management',
        icon: 'icon_abb_dias icon-abb-user-managment-repository',
        path: '/UserManagement'
      });
    }

    // Determine which item should be selected based on current pathname
    let selectedItem: any = undefined;
    if (location?.pathname === '/') {
      selectedItem = siteDashboardNavItem;
    } else if (location?.pathname === '/UserManagement') {
      selectedItem = sidebarItems.find((item: any) => item.path === '/UserManagement');
    }

    return (
      <div>
        <Header
          applicationName="ABB ServicePort Licensing"
          hasHamburgerMenu={showSidebar}
          isNavOpened={this.state.isSidebarOpen}
          breadcrumb={isLoginPage ? undefined : (
            <MUIBreadcrumb
              currentPage={breadcrumbConfig.currentPage}
              onClickBreadcrumb={this.onClickBreadcrumb}
              previousPages={breadcrumbConfig.previousPages}
            />
          )}
          logoSrc='/logo.svg'
          onClickLogo={this.goHome}
          onHamburgerMenuClick={this.onHamburgerMenuClick}
          parentClassName="app-header"
          subtitle="LPM"
        />
        <div className='app-version-info'>
          <span className='app-version-item'>UI v{this.state.uiVersion}</span>
          <span className='app-version-separator'>|</span>
          <span className='app-version-item'>API v{this.state.apiVersion}</span>
        </div>
        {!isLoginPage && this.props.loggedInUser && (
          <div className='avatar-menu-container' ref={this.avatarMenuRef}>
            <button
              type='button'
              className='avatar-menu-trigger'
              onClick={this.toggleAvatarMenu}
            >
              <div className='avatar-circle'>
                {userProfile?.name ? userProfile.name[0]?.toUpperCase() : 'U'}
              </div>
            </button>
            {this.state.isAvatarMenuOpen && (
              <div className='avatar-dropdown-menu'>
                <div className='avatar-menu-item user-info'>
                  <div className='user-name'>{userProfile?.name || 'User'}</div>
                  <div className='user-email'>{userProfile?.email || this.props.loggedInUser.email || 'N/A'}</div>
                </div>
                <div className='avatar-menu-divider'></div>
                <button
                  type='button'
                  className='avatar-menu-item logout-item'
                  onClick={this.handleLogout}
                >
                  Sign out
                </button>
              </div>
            )}
          </div>
        )}
        <div className='layout-shell'>
          {showSidebar ? (
            <div className='layout-sidebar'>
              <LeftNavigation
                isNavOpen={this.state.isSidebarOpen}
                navbarContents={sidebarItems}
                onClickNavItem={this.onClickLeftNavigationItem}
                parentClassname='layout-left-navigation'
                selectedItem={selectedItem}
              />
            </div>
          ) : null}
          <div className='layout-content container-fluid'>
            {this.props.children}
          </div>
        </div>
      </div>
    );
  }
}
export default withRouter(connect(mapStateToProps)(LayoutComponent) as any);

function mapStateToProps(store: any) {
  return {
    loggedInUser: store.userState.loggedInUser,
    userPermissions: store.userState.userPermissions,
    currentSiteName: store.servicesState?.site?.siteName
  };
}

